import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface HeroSlide {
  id: string;
  title: string;
  tagline: string;
  intro: string;
  genres: string[];
  banner: string;
  rating: number;
}

const slides: HeroSlide[] = [
  {
    id: "night-garden",
    title: "夜色花园的她们",
    tagline: "校园 · 治愈 · 双向奔赴",
    intro: "转学生与学姐在月光下的花园相遇，一段温柔克制的双女主恋曲悄然绽放。",
    genres: ["校园", "恋爱", "治愈"],
    banner:
      "https://readdy.ai/api/search-image?query=Atmospheric%20stylized%20illustration%20of%20a%20moonlit%20night%20garden%20with%20two%20young%20women%20silhouettes%20among%20glowing%20white%20flowers%2C%20deep%20sky%20blue%20tonight%20tones%20with%20warm%20amber%20lantern%20light%2C%20soft%20watercolor%20texture%2C%20dreamy%20romantic%20mood%2C%20painterly%20artistic%20webtoon%20banner%2C%20rich%20contrast%20for%20text%20overlay&width=1600&height=900&seq=hero-night-garden&orientation=landscape",
    rating: 9.6,
  },
  {
    id: "duchess-contract",
    title: "契约女公爵",
    tagline: "奇幻 · 宫廷 · 强强",
    intro: "一纸婚约将落魄千金与冷面女公爵绑在一起，假戏真做的宫廷恋曲就此开场。",
    genres: ["奇幻", "恋爱", "古风"],
    banner:
      "https://readdy.ai/api/search-image?query=Opulent%20stylized%20illustration%20of%20two%20elegant%20noblewomen%20in%20flowing%20gowns%20dancing%20in%20a%20grand%20marble%20ballroom%2C%20golden%20chandelier%20glow%2C%20rich%20sky%20blue%20and%20warm%20amber%20palette%2C%20painterly%20artistic%20fantasy%20webtoon%20banner%2C%20cinematic%20depth%20and%20contrast%20for%20text%20overlay&width=1600&height=900&seq=hero-duchess&orientation=landscape",
    rating: 9.8,
  },
  {
    id: "sword-and-rose",
    title: "剑与玫瑰之约",
    tagline: "奇幻 · 动作 · 热血",
    intro: "宿敌女骑士在并肩作战中成为彼此最信任的后背，热血与心动并存的骑士物语。",
    genres: ["奇幻", "动作", "恋爱"],
    banner:
      "https://readdy.ai/api/search-image?query=Epic%20stylized%20illustration%20of%20two%20female%20knights%20standing%20back%20to%20back%20on%20a%20battlefield%20at%20sunset%20with%20blooming%20roses%2C%20dramatic%20sky%20blue%20sky%20blending%20into%20warm%20orange%20light%2C%20painterly%20artistic%20fantasy%20webtoon%20banner%2C%20strong%20contrast%20for%20text%20overlay&width=1600&height=900&seq=hero-sword&orientation=landscape",
    rating: 9.7,
  },
  {
    id: "under-the-stars",
    title: "星辰之下共眠",
    tagline: "奇幻 · 冒险 · 宿命",
    intro: "星象学者与流浪剑姬结伴远行，在星海尽头成为彼此唯一的坐标。",
    genres: ["奇幻", "科幻", "恋爱"],
    banner:
      "https://readdy.ai/api/search-image?query=Ethereal%20stylized%20illustration%20of%20two%20women%20travelers%20resting%20on%20a%20hill%20under%20a%20vast%20starry%20sky%20with%20glowing%20constellations%2C%20deep%20sky%20blue%20with%20warm%20magical%20highlights%2C%20painterly%20artistic%20fantasy%20webtoon%20banner%2C%20cinematic%20contrast%20for%20text%20overlay&width=1600&height=900&seq=hero-stars&orientation=landscape",
    rating: 9.5,
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5200);
    return () => clearInterval(timer);
  }, []);

  const goTo = (next: number) => setIndex((next + slides.length) % slides.length);
  const current = slides[index];

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="relative h-[460px] w-full md:h-[600px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.banner}
              alt={`${slide.title} 双女主韩漫推荐`}
              title={`${slide.title} 韩漫在线阅读`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
          <div className="max-w-xl">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-line" /> 本周热读 · 双女主推荐
            </span>
            <h2 className="font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
              {current.title}
            </h2>
            <p className="mt-3 text-sm text-primary-200 md:text-base">{current.tagline}</p>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
              {current.intro}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {current.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur"
                >
                  {genre}
                </span>
              ))}
              <span className="flex items-center gap-1 text-sm font-medium text-accent-300">
                <i className="ri-star-fill" /> {current.rating}
              </span>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-book-read-line" /> 立即阅读
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-6 py-3 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20"
              >
                <i className="ri-compass-3-line" /> 探索更多分类
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 md:left-6 md:h-11 md:w-11"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 md:right-6 md:h-11 md:w-11"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(i)}
              className={`h-2 cursor-pointer rounded-full transition-all ${
                i === index ? "w-7 bg-accent-500" : "w-2 bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`切换到第 ${i + 1} 张`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}